import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useConnectedAgents } from '../../api/account-queries';
import { useMe } from '../../api/onboarding-queries';
import { useWorkspaces } from '../../api/queries';
import { useDeleteAccount, useDeleteWorkspace } from '../../api/settings-queries';
import { useSession } from '../../app/session-context';
import { ErrorBanner } from '../../components/ErrorBanner';
import { useAnnounce } from '../../ui/Announcer';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { Dialog } from '../../ui/Dialog';
import { InputField } from '../../ui/Input';
import { Stack } from '../../ui/Stack';
import { StatusState } from '../../ui/StatusState';

export const WORKSPACE_DELETE_TEXT =
  'Deletes {name}, with its reports, data, report versions, brand and API tokens. Published links stop working at once. ' +
  "This can't be undone from the app. A pseudonymised activity log and a record that the deletion happened are kept.";

const DEFAULT_WORKSPACE_ID = 'ws_default';

/** Destructive actions, each confirmed by typing. Admin mode has none: one server-owned workspace. */
export function DangerZone(): ReactElement {
  const { snapshot } = useSession();
  if (snapshot.mode !== 'accounts') {
    return <StatusState kind="empty" title="Nothing here can be undone yet." />;
  }
  return (
    <Stack gap={4}>
      {snapshot.workspaceId && snapshot.workspaceId !== DEFAULT_WORKSPACE_ID ? (
        <WorkspaceCard workspaceId={snapshot.workspaceId} />
      ) : null}
      {snapshot.config?.accountDeletion === true ? (
        <AccountCard mcpOauth={snapshot.config.mcpOauth === true} />
      ) : null}
    </Stack>
  );
}

/** A dialog whose confirm button stays disabled until the typed text passes `matches`. */
function TypedConfirmDialog(props: {
  title: string;
  fieldLabel: string;
  confirmLabel: string;
  matches: (typed: string) => boolean;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: (typed: string) => void;
  children: ReactNode;
}): ReactElement {
  const [typed, setTyped] = useState('');
  return (
    <Dialog
      title={props.title}
      isOpen
      onOpenChange={(open) => {
        if (!open && !props.busy) props.onClose();
      }}
      footer={
        <>
          <Button variant="secondary" isDisabled={props.busy} onPress={props.onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            busy={props.busy}
            isDisabled={!props.matches(typed)}
            onPress={() => props.onConfirm(typed)}
          >
            {props.confirmLabel}
          </Button>
        </>
      }
    >
      <Stack gap={4}>
        {props.children}
        <InputField label={props.fieldLabel} value={typed} onChange={setTyped} autoFocus />
        <ErrorBanner error={props.error} />
      </Stack>
    </Dialog>
  );
}

function WorkspaceCard({ workspaceId }: { workspaceId: string }): ReactElement {
  const { store } = useSession();
  const navigate = useNavigate();
  const announce = useAnnounce();
  const workspaces = useWorkspaces();
  const remove = useDeleteWorkspace();
  const [open, setOpen] = useState(false);
  const name = workspaces.data?.find((w) => w.id === workspaceId)?.name;

  const close = (): void => {
    setOpen(false);
    remove.reset();
  };

  return (
    <Card title="Delete this workspace">
      <Stack gap={4}>
        <ErrorBanner error={workspaces.error} />
        <div>
          <Button variant="danger" isDisabled={name === undefined} onPress={() => setOpen(true)}>
            Delete this workspace
          </Button>
        </div>
      </Stack>
      {open && name !== undefined ? (
        <TypedConfirmDialog
          title="Delete this workspace"
          fieldLabel="Type the workspace name to confirm"
          confirmLabel="Delete workspace"
          matches={(typed) => typed === name}
          busy={remove.isPending}
          error={remove.error}
          onClose={close}
          onConfirm={(typed) =>
            remove.mutate(
              { id: workspaceId, confirmName: typed },
              {
                onSuccess: () => {
                  store.forgetDeletedWorkspace(workspaceId);
                  void navigate({ to: '/' });
                  announce(`Deleted ${name}.`);
                }
              }
            )
          }
        >
          <p className="m-0">{WORKSPACE_DELETE_TEXT.replace('{name}', name)}</p>
        </TypedConfirmDialog>
      ) : null}
    </Card>
  );
}

function AccountCard({ mcpOauth }: { mcpOauth: boolean }): ReactElement {
  const { store } = useSession();
  const navigate = useNavigate();
  const me = useMe();
  const workspaces = useWorkspaces();
  const agents = useConnectedAgents(mcpOauth);
  const remove = useDeleteAccount();
  const [open, setOpen] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const email = me.data?.label;

  const close = (): void => {
    setOpen(false);
    remove.reset();
  };

  return (
    <Card title="Delete your account">
      <Stack gap={4}>
        <ErrorBanner error={me.error} />
        <div>
          <Button variant="danger" isDisabled={email === undefined} onPress={() => setOpen(true)}>
            Delete your account
          </Button>
        </div>
      </Stack>
      {open && email !== undefined ? (
        <TypedConfirmDialog
          title="Delete your account"
          fieldLabel="Type your email address to confirm"
          confirmLabel="Delete account"
          matches={(typed) => typed.trim().toLowerCase() === email.trim().toLowerCase()}
          busy={remove.isPending || finishing}
          error={remove.error}
          onClose={close}
          onConfirm={(typed) =>
            remove.mutate(typed, {
              onSuccess: async () => {
                setFinishing(true);
                try {
                  await store.signOut();
                } catch {
                  // The account is gone either way; leaving the page must not depend on sign-out cleanup.
                } finally {
                  void navigate({ to: '/account-deleted' });
                }
              }
            })
          }
        >
          <Stack gap={2}>
            <p className="m-0">Deleting your account removes your sign-in and the workspaces you own.</p>
            <p className="m-0">Workspaces you own are deleted with your account. Workspaces you only belong to are not deleted; you just leave them.</p>
            <p className="m-0">Your workspaces</p>
            <ul className="m-0">
              {(workspaces.data ?? []).map((w) => (
                <li key={w.id}>{w.name}</li>
              ))}
            </ul>
            {mcpOauth && agents.data && agents.data.length > 0 ? (
              <>
                <p className="m-0">Connected agents</p>
                <ul className="m-0">
                  {agents.data.map((a) => (
                    <li key={a.id}>{a.clientName ?? 'Unnamed agent'}</li>
                  ))}
                </ul>
              </>
            ) : null}
            <p className="m-0">Your sign-in is removed.</p>
          </Stack>
        </TypedConfirmDialog>
      ) : null}
    </Card>
  );
}
